import React, { useEffect, useState } from 'react';
import { Stack, DefaultButton, Text, Link, MessageBar, MessageBarType } from '@fluentui/react';
import styles from './CircleLogin.module.css';
import shiftworksLogo from '../../assets/shiftworks-logo-pink.png';

const CircleLogin = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [authAttempted, setAuthAttempted] = useState<boolean>(false);

  useEffect(() => {
    // First, check if user already has a valid authentication session
    checkExistingAuth();
    
    // Check if user was redirected back from a failed auth attempt
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const authFailed = urlParams.get('auth_failed');
    
    if (error || authFailed) {
      setErrorMessage(
        'Authentication failed. If you\'re not a King\'s Fund staff member, please use the "Sign in as an external member" option below.'
      );
      setAuthAttempted(true);
    }

    // Check if coming back from Easy Auth with no user (failed authorization)
    checkAuthStatus();
  }, []);

  const checkExistingAuth = async () => {
    // Check if user already has valid auth (Azure AD or Circle)
    console.log('🔍 Checking for existing authentication...');
    
    try {
      // First check our unified endpoint (handles both Circle and Easy Auth)
      const response = await fetch('/auth/me');
      if (response.ok) {
        // User is already authenticated
        console.log('✅ User already authenticated via /auth/me, redirecting to home');
        window.location.href = '/';
        return;
      }
      console.log('❌ /auth/me returned not authenticated');
    } catch (error) {
      console.log('⚠️ Error checking /auth/me:', error);
    }
    
    // Try Easy Auth endpoint (may have session from ShiftWorks or previous login)
    try {
      console.log('🔍 Checking Easy Auth (/.auth/me)...');
      const easyAuthResponse = await fetch('/.auth/me');
      if (easyAuthResponse.ok) {
        const data = await easyAuthResponse.json();
        console.log('Easy Auth response:', data);
        
        if (data && data.length > 0 && data[0].user_id) {
          // Easy Auth has a valid session (from Azure AD)
          console.log('✅ Found Easy Auth session, user is authenticated! Redirecting...');
          // Redirect to trigger app to pick up the Easy Auth session
          window.location.href = '/';
          return;
        }
      }
      console.log('❌ No valid Easy Auth session found');
    } catch (error) {
      console.log('⚠️ Error checking Easy Auth:', error);
    }
    
    // No existing auth found, user will see login page
    console.log('ℹ️ No existing authentication found - showing login page');
  };

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/.auth/me');
      if (response.ok) {
        const data = await response.json();
        // If Easy Auth has session but user not authorized, show error
        if (data && data.length === 0) {
          const referrer = document.referrer;
          if (referrer && referrer.includes('login.microsoftonline.com')) {
            setErrorMessage(
              'Your Microsoft account was authenticated, but you don\'t have access to this application. If you\'re an external member, please use the "Sign in as an external member" option below.'
            );
            setAuthAttempted(true);
          }
        }
      }
    } catch (error) {
      // Silent fail - just means no auth session
    }
  };

  const handleStaffLogin = async () => {
    // King's Fund staff use Microsoft/Azure AD
    // First, check if Easy Auth is available
    try {
      const checkAuth = await fetch('/.auth/me');
      if (checkAuth.status === 404) {
        // Easy Auth not configured, show error
        setErrorMessage(
          'Microsoft authentication is not currently configured. Please contact your administrator or use the "Sign in as an external member" option below.'
        );
        return;
      }
    } catch (error) {
      // Easy Auth might not be available
      setErrorMessage(
        'Microsoft authentication is not currently available. Please contact your administrator or use the "Sign in as an external member" option below.'
      );
      return;
    }
    
    // Easy Auth is available, proceed with login
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=/';
  };

  const handleExternalLogin = () => {
    // External members use Circle - redirect to Circle's hosted login
    window.location.href = 'https://shiftworks.circle.so/sign_in';
  };

  return (
    <div className={styles.loginContainer}>
      <Stack className={styles.loginCard} tokens={{ childrenGap: 24 }}>
        {/* Logo */}
        <div className={styles.logoContainer}>
          <img 
            src={shiftworksLogo}
            alt="ShiftWorks by The King's Fund" 
            className={styles.logo}
          />
        </div>

        {/* Heading */}
        <div className={styles.loginHeader}>
          <Text variant="xLarge" className={styles.loginTitle}>
            Log in to your account
          </Text>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <MessageBar
            messageBarType={MessageBarType.warning}
            isMultiline={true}
            onDismiss={() => setErrorMessage(null)}
            dismissButtonAriaLabel="Close"
          >
            {errorMessage}
          </MessageBar>
        )}

        {/* Login Buttons */}
        <Stack tokens={{ childrenGap: 16 }}>
          <DefaultButton
            text="Continue with a King's Fund staff account"
            onClick={handleStaffLogin}
            className={styles.staffButton}
          />
          <DefaultButton
            text="Sign in as an external member"
            onClick={handleExternalLogin}
            className={styles.externalButton}
          />
        </Stack>

        {/* Forgot Password Link */}
        <div className={styles.forgotPassword}>
          <Link 
            href="https://shiftworks.circle.so/forgot_password"
            target="_blank"
            className={styles.forgotLink}
          >
            Forgot your password?
          </Link>
        </div>
      </Stack>
    </div>
  );
};

export default CircleLogin;

